import { apiClient } from "@/lib/api";
import type { ProtocolStatus } from "@/types";

export interface ProtocolTag {
  id: string;
  name: string;
}

export interface ProtocolVersion {
  id: string;
  version: string;
  original_filename: string;
  change_summary: string;
  created_by_user_id: string;
  created_at: string;
  is_current: boolean;
}

export interface Protocol {
  id: string;
  title: string;
  category: string | null;
  status: ProtocolStatus;
  tags: ProtocolTag[];
  current_version: ProtocolVersion;
  created_at: string;
  updated_at: string;
}

export interface ProtocolListResponse {
  items: Protocol[];
}

export interface ProtocolTagListResponse {
  items: ProtocolTag[];
}

export interface ProtocolVersionsResponse {
  items: ProtocolVersion[];
}

export interface ProtocolListParams {
  status?: ProtocolStatus;
  category?: string;
  q?: string;
  /** Repeated `tag_id` query params; document matches any selected tag. */
  tag_id?: string[];
}

export interface ProtocolCreatePayload {
  title: string;
  file: File;
  category: string | null;
  status: ProtocolStatus;
  tag_ids: string[];
  tag_names: string[];
}

export interface ProtocolUpdatePayload {
  title: string;
  category: string | null;
  status: ProtocolStatus;
  tag_ids: string[];
  tag_names: string[];
}

export interface ProtocolPublishVersionPayload {
  file: File;
  change_summary: string;
  version_bump: "MINOR" | "MAJOR";
}

export const protocolService = {
  async list(params: ProtocolListParams = {}): Promise<ProtocolListResponse> {
    const { data } = await apiClient.get<ProtocolListResponse>(
      "/admin/protocols",
      { params },
    );
    return data;
  },

  async listTags(): Promise<ProtocolTagListResponse> {
    const { data } = await apiClient.get<ProtocolTagListResponse>(
      "/admin/protocols/tags",
    );
    return data;
  },

  async createTag(name: string): Promise<ProtocolTag> {
    const { data } = await apiClient.post<ProtocolTag>(
      "/admin/protocols/tags",
      { name },
    );
    return data;
  },

  async create(payload: ProtocolCreatePayload): Promise<Protocol> {
    const form = new FormData();
    form.append("title", payload.title);
    form.append("file", payload.file);
    form.append("status", payload.status);
    if (payload.category != null) {
      form.append("category", payload.category);
    }
    for (const tagId of payload.tag_ids) {
      form.append("tag_ids", tagId);
    }
    for (const tagName of payload.tag_names) {
      form.append("tag_names", tagName);
    }
    const { data } = await apiClient.post<Protocol>("/admin/protocols", form);
    return data;
  },

  async update(
    protocolId: string,
    payload: ProtocolUpdatePayload,
  ): Promise<Protocol> {
    const { data } = await apiClient.patch<Protocol>(
      `/admin/protocols/${protocolId}`,
      payload,
    );
    return data;
  },

  async listVersions(protocolId: string): Promise<ProtocolVersionsResponse> {
    const { data } = await apiClient.get<ProtocolVersionsResponse>(
      `/admin/protocols/${protocolId}/versions`,
    );
    return data;
  },

  async publishVersion(
    protocolId: string,
    payload: ProtocolPublishVersionPayload,
  ): Promise<Protocol> {
    const form = new FormData();
    form.append("file", payload.file);
    form.append("change_summary", payload.change_summary);
    form.append("version_bump", payload.version_bump);
    const { data } = await apiClient.post<Protocol>(
      `/admin/protocols/${protocolId}/versions`,
      form,
    );
    return data;
  },

  async downloadVersionFile(
    protocolId: string,
    versionId: string,
  ): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `/admin/protocols/${protocolId}/versions/${versionId}/file`,
      { responseType: "blob" },
    );
    return data;
  },
};
